import React, { useState } from "react";
import { Mail, Download, MessageCircle, Send, Share2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { socialLinks, canNativeShare, nativeShare } from "@/lib/share";
import { warn } from "@/lib/log";

const API = process.env.REACT_APP_BACKEND_URL;

async function fetchBlob(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error("postcard");
  return r.blob();
}

function PostcardActions({ item, imgUrl, text, lang }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const links = socialLinks(imgUrl, text);
  const track = (method) => trackEvent("share", { method, content_type: "postcard", item_id: item.id, lang });
  const fileName = `postcard-${item.country_code}.png`;

  const download = async () => {
    track("download"); setBusy(true);
    try {
      const blob = await fetchBlob(imgUrl);
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: fileName });
      a.click(); URL.revokeObjectURL(a.href);
    } catch (err) { warn("postcard download fallback:", err); window.open(`${imgUrl}&download=1`, "_blank"); }
    setBusy(false);
  };

  const share = async () => {
    track("native"); setBusy(true);
    try {
      const file = new File([await fetchBlob(imgUrl)], fileName, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await nativeShare({ files: [file], title: text, text });
      else await nativeShare({ title: text, text, url: imgUrl });
    } catch (err) { if (err?.name !== "AbortError") warn("postcard share failed:", err); }
    setBusy(false);
  };

  const btn = "flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors";
  return (
    <div className="grid grid-cols-2 gap-2">
      <button onClick={download} disabled={busy} className={`${btn} bg-ink text-sand hover:bg-ink/90 col-span-2`} data-testid="postcard-download">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("postcard_download")}
      </button>
      <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp")} className={`${btn} bg-[#25D366]/15 text-[#128C7E] hover:bg-[#25D366]/25`} data-testid="postcard-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
      <a href={links.telegram} target="_blank" rel="noopener noreferrer" onClick={() => track("telegram")} className={`${btn} bg-[#229ED9]/15 text-[#1a7fb0] hover:bg-[#229ED9]/25`} data-testid="postcard-telegram"><Send className="w-4 h-4" /> Telegram</a>
      {canNativeShare() && (
        <button onClick={share} disabled={busy} className={`${btn} bg-ochre text-white hover:bg-ochre/90 col-span-2`} data-testid="postcard-native"><Share2 className="w-4 h-4" /> {t("share")}</button>
      )}
    </div>
  );
}

/** "Postcard" button → dialog with the server-rendered 1080×1350 postcard (download / WhatsApp / Telegram / native share). */
export default function Postcard({ item, countryName, dark = false, size = "sm", className = "", testid }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (!item) return null;

  const imgUrl = `${API}/api/postcard/${item.id}.png?lang=${lang}`;
  const text = t("postcard_text").replace("{country}", countryName || item.country_code);
  const btn = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const tone = dark ? "bg-sand/15 hover:bg-sand/30 text-sand" : "bg-card/90 border border-stoneLine text-ink hover:border-ochre hover:text-ochre backdrop-blur-sm";

  return (
    <>
      <button type="button" aria-label={t("postcard")} title={t("postcard")} data-testid={testid || `postcard-btn-${item.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); trackEvent("postcard_open", { country_code: item.country_code }); }}
        className={`${btn} rounded-full flex items-center justify-center transition-colors ${tone} ${className}`}>
        <Mail className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl z-[90]" onClick={(e) => e.stopPropagation()} onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.stopPropagation()} data-testid="postcard-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("postcard")}</DialogTitle>
            <DialogDescription>{t("postcard_sub")}</DialogDescription>
          </DialogHeader>
          <div className="relative rounded-xl overflow-hidden border border-stoneLine bg-sandDeep/40 aspect-[4/5] max-h-[50vh] mx-auto w-full">
            {!loaded && <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>}
            <img src={imgUrl} alt="" onLoad={() => setLoaded(true)} className={`w-full h-full object-contain transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`} data-testid="postcard-image" />
          </div>
          <PostcardActions item={item} imgUrl={imgUrl} text={text} lang={lang} />
        </DialogContent>
      </Dialog>
    </>
  );
}
